import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { Button, Card, LinkButton, Notice, TextField, buttonClass } from './ui.js';

const MAX_BYTES = 256 * 1024;
const TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const BRANDING_KEY = ['account', 'report-branding'] as const;

/** The logo and name used on the printable site report (T-66, Business and Agency). */
export function useReportBranding() {
  return useQuery({
    queryKey: BRANDING_KEY,
    queryFn: () => unwrap(api.GET('/account/report-branding')),
  });
}

const readAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(file);
  });

/** Account settings: report name and logo (upload, preview, remove; account.html). */
export function ReportBrandingCard() {
  const { t } = useTranslation();
  const client = useQueryClient();
  const branding = useReportBranding();
  const [name, setName] = useState('');
  // undefined: keep the stored logo; null: remove it; string: the new one (data: URL).
  const [logo, setLogo] = useState<string | null | undefined>(undefined);
  const [fileError, setFileError] = useState<string>();
  useEffect(() => {
    if (branding.data) setName(branding.data.name ?? '');
  }, [branding.data]);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.PUT('/account/report-branding', {
          body: { name: name.trim() || null, ...(logo === undefined ? {} : { logo }) },
        }),
      ),
    onSuccess: (data) => {
      client.setQueryData(BRANDING_KEY, data);
      setLogo(undefined);
    },
  });
  if (!branding.data) return null;
  const b = branding.data;
  const preview = logo === undefined ? b.logo : logo;

  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!TYPES.includes(file.type)) return setFileError(t('branding.wrongType'));
    if (file.size > MAX_BYTES) return setFileError(t('branding.tooLarge'));
    setFileError(undefined);
    setLogo(await readAsDataUrl(file));
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <Card>
      <h2>
        {t('branding.title')} <small>{t('branding.intro')}</small>
      </h2>
      {!b.available ? (
        <div className="upsell">
          <span>{t('branding.planNeeded')}</span>
          <LinkButton to="/billing" variant="secondary" size="sm">
            {t('branding.upgrade')}
          </LinkButton>
        </div>
      ) : (
        <form onSubmit={submit}>
          <div className="field-narrow">
            <TextField
              label={t('branding.name')}
              hint={t('branding.nameHint')}
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="logo-box">
            <div className="prev">
              {preview ? <img src={preview} alt={t('branding.logoAlt')} /> : null}
            </div>
            <div>
              <div className="acts">
                {/* A file field drawn as the sheet's button; its focus shows on the button. */}
                <label className={`${buttonClass('secondary', 'sm')} file`}>
                  {preview ? t('branding.replace') : t('branding.upload')}
                  <input
                    type="file"
                    accept={TYPES.join(',')}
                    className="sr-only"
                    onChange={(e) => void pick(e)}
                  />
                </label>
                {preview ? (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setLogo(null)}>
                    {t('branding.remove')}
                  </Button>
                ) : null}
              </div>
              <span className="hint">{t('branding.logoHint')}</span>
            </div>
          </div>
          {fileError ? <Notice kind="error">{fileError}</Notice> : null}
          {save.isError ? <Notice kind="error">{errorMessage(save.error, t)}</Notice> : null}
          <div className="acts">
            <Button type="submit" busy={save.isPending}>
              {t('app.save')}
            </Button>
            {save.isSuccess ? (
              <span role="status" className="hint">
                {t('app.saved')}
              </span>
            ) : null}
          </div>
        </form>
      )}
    </Card>
  );
}
